import React, { useEffect, useState } from "react";
import { TopBar } from "@/components/TopBar";
import { ProductImage } from "@/components/ProductImage";
import { MainAction } from "@/components/MainAction";
import { InquirySheet } from "@/components/InquirySheet";
import { EmptyState } from "@/components/EmptyState";
import { useI18n } from "@/i18n";
import { useRouter } from "@/router/Router";
import { useCart } from "@/state/CartContext";
import { fetchProduct, fetchReviews, mediaUrl } from "@/lib/api";
import { ReviewCard } from "@/components/ReviewCard";
import { productTag, toneFor, unitsLeft } from "@/components/ProductCard";
import { OriginMap, findOrigin } from "@/components/OriginMap";
import { CompareBar } from "@/components/CompareBar";
import { ProductPageSkeleton } from "@/components/Skeleton";
import { useCompare } from "@/lib/compare";
import { FlavorProfile, RoastDiamonds } from "@/components/Flavor";
import { PriceWatchToggle, ShareRow } from "@/components/Growth";
import { track } from "@/lib/analytics";
import { categoryLabel } from "@/lib/categories";
import { flyToCart } from "@/lib/motion";
import { SubscribeSheet } from "@/components/Subscriptions";
import { useConfig } from "@/lib/useConfig";
import { formatUzs } from "@/lib/format";
import type { Product, Review } from "@/types";
import { useBackButton } from "@/lib/hooks";
import { haptic } from "@/lib/telegram";
import { Ornament, PriceTag } from "@/components/Premium";

export function ProductPage({ slug }: { slug: string }) {
  const { t, locale } = useI18n();
  const { navigate, goBack } = useRouter();
  const { addItem, isFavorite, toggleFavorite } = useCart();
  const [product, setProduct] = useState<Product | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [variantId, setVariantId] = useState<string | null>(null);
  const [activeImage, setActiveImage] = useState(0);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewsTotal, setReviewsTotal] = useState(0);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const { features, scarcityThreshold, socialProofMin } = useConfig();
  const compare = useCompare();
  const [justAdded, setJustAdded] = useState(false);
  useEffect(() => setJustAdded(false), [slug]);
  useEffect(() => {
    if (product) track("view_product", product.id);
  }, [product?.id]);

  // Only the 3 latest reviews here, to keep the card short; the rest are one tap away.
  useEffect(() => {
    fetchReviews(slug, 3)
      .then((r) => {
        setReviews(r.items);
        setReviewsTotal(r.total);
      })
      .catch(() => setReviews([]));
  }, [slug]);

  useEffect(() => {
    let cancelled = false;
    setProduct(null);
    setNotFound(false);
    fetchProduct(slug)
      .then((p) => {
        if (cancelled) return;
        if (!p) {
          setNotFound(true);
          return;
        }
        setProduct(p);
        setVariantId(p.variants[0]?.id ?? null);
        setActiveImage(0);
      })
      .catch(() => !cancelled && setNotFound(true));
    return () => {
      cancelled = true;
    };
  }, [slug, locale]);

  // "Back" closes the request form first, then leaves the page.
  useBackButton(() => (subscribeOpen ? setSubscribeOpen(false) : zoomOpen ? setZoomOpen(false) : inquiryOpen ? setInquiryOpen(false) : goBack()));

  const variant = product?.variants.find((v) => v.id === variantId) ?? product?.variants[0];
  const isUnderOrder = product?.orderMode === "under_order";
  const inStock = !!variant && variant.stock > 0;

  const handlePrimary = () => {
    if (!product || !variant) return;
    if (isUnderOrder) {
      haptic("light");
      setInquiryOpen(true);
      return;
    }
    if (!inStock) {
      // Out of stock: "tell me when it's back" request.
      haptic("light");
      setInquiryOpen(true);
      return;
    }
    if (justAdded) {
      navigate({ name: "cart" });
      return;
    }
    flyToCart(document.querySelector(".product-detail__frame .product-hero-image"));
    addItem(product.id, variant.id);
    haptic("success");
    setJustAdded(true);
  };

  const primaryText = isUnderOrder ? t("inquiry.title") : !inStock ? t("restock.button") : justAdded ? t("product.goToCart") : t("product.addToCart");

  if (notFound) {
    return (
      <>
        <TopBar title="" onBack={goBack} />
        <main className="page page--no-nav">
          <EmptyState title={t("catalog.empty")} action={<button className="btn btn--primary" onClick={() => navigate({ name: "catalog" })}>{t("cart.goToCatalog")}</button>} />
        </main>
      </>
    );
  }

  if (!product || !variant) {
    return (
      <>
        <TopBar title="" onBack={goBack} />
        <main className="page page--no-nav">
          <ProductPageSkeleton />
        </main>
      </>
    );
  }

  return (
    <>
      <TopBar title={product.name} onBack={goBack} />
      <main className="page page--no-nav">
        <div className="product-detail__frame" data-tone={toneFor(product.id)}>
          <button className="zoom-trigger" onClick={() => product.images[activeImage] && setZoomOpen(true)} aria-label={t("product.zoom")}>
            <ProductImage src={product.images[activeImage]} alt={product.name} category={product.category} className="product-hero-image" />
          </button>
          {product.images.length > 1 && (
            <span className="gallery-count" aria-hidden="true">
              {String(activeImage + 1).padStart(2, "0")} / {String(product.images.length).padStart(2, "0")}
            </span>
          )}
          <button
            onClick={() => toggleFavorite(product.id)}
            aria-label={t("product.addToFavorites")}
            aria-pressed={isFavorite(product.id)}
            className="favorite-toggle"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill={isFavorite(product.id) ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.8">
              <path d="M12 20.5s-7.5-4.6-10-9.3C.4 8 2 4.6 5.4 4a5 5 0 0 1 6.6 2.1A5 5 0 0 1 18.6 4C22 4.6 23.6 8 22 11.2 19.5 15.9 12 20.5 12 20.5Z" />
            </svg>
          </button>
        </div>

        {product.images.length > 1 && (
          <div className="product-thumbs">
            {product.images.map((img, i) => (
              <button key={img + i} onClick={() => setActiveImage(i)} data-active={i === activeImage} aria-label={`${i + 1}`}>
                <ProductImage src={img} alt="" category={product.category} className="product-card__image" />
              </button>
            ))}
          </div>
        )}

        <div className="product-card__meta">
          <span className="eyebrow">{categoryLabel(product, t)}</span>
          <RoastDiamonds level={product.flavor?.roastLevel} withLabel />
        </div>
        <h1 className="product-detail__title">{product.name}</h1>
        {!!product.flavor?.notes?.length && (
          <div className="product-detail__notes">{product.flavor.notes.map((n) => t(`flavor.notes.${n}`).toLowerCase()).join(", ")}</div>
        )}
        {!!product.rating && (
          <div className="product-detail__rating">
            <b>★</b> {product.rating.toFixed(1)} · {t("product.reviews", { count: product.reviewsCount ?? 0 })}
          </div>
        )}
        {(() => {
          const tag = productTag(product, t);
          const left = unitsLeft(product);
          const scarce = !isUnderOrder && scarcityThreshold > 0 && left > 0 && left <= scarcityThreshold;
          const proof = (product.soldLastWeek ?? 0) >= socialProofMin;
          if (!tag && !scarce && !proof) return null;
          return (
            <div className="product-signals">
              {tag && !isUnderOrder && <span className="badge badge--muted">{tag}</span>}
              {scarce && <span className="badge badge--danger">{t("product.unitsLeft", { count: left })}</span>}
              {proof && <span className="product-signals__proof">{t("product.soldWeek", { count: product.soldLastWeek ?? 0 })}</span>}
            </div>
          );
        })()}

        {isUnderOrder ? (
          <div style={{ marginBottom: 12 }}>
            {product.variants[0]?.priceUzs > 0 && (
              <div className="product-detail__price" style={{ marginBottom: 6 }}>
                <PriceTag uzs={product.variants[0].priceUzs} />
              </div>
            )}
            <span className="badge badge--muted">{t("catalog.underOrder")}</span>
            {product.variants[0]?.priceUzs > 0 && <p className="hint-line" style={{ margin: "8px 0 0" }}>{t("catalog.underOrderHint")}</p>}
          </div>
        ) : (
          <>
            {product.variants.length > 1 && (
              <div className="segmented" style={{ marginBottom: "var(--space-4)" }}>
                {product.variants.map((v) => (
                  <button key={v.id} className="chip" data-active={v.id === variant.id} onClick={() => setVariantId(v.id)}>
                    {v.label}
                  </button>
                ))}
              </div>
            )}
            <div className="product-detail__price">
              <PriceTag uzs={variant.priceUzs} />
              {!!variant.oldPriceUzs && variant.oldPriceUzs > variant.priceUzs && (
                <span className="price price--old">{formatUzs(variant.oldPriceUzs, locale)}</span>
              )}
            </div>
            {!inStock && <div style={{ color: "var(--color-danger)", fontSize: 13, marginBottom: 8 }}>{t("product.outOfStock")}</div>}
          </>
        )}

        {features.subscriptions && product.category === "coffee" && !isUnderOrder && inStock && (
          <button className="btn btn--ghost" style={{ width: "100%", marginTop: 12 }} onClick={() => setSubscribeOpen(true)}>
            ↻ {t("subscription.cta")}
          </button>
        )}

        <ShareRow product={product} />
        {!isUnderOrder && <PriceWatchToggle productId={product.id} />}
        {isUnderOrder && (
          <button className="card biz-row" onClick={() => navigate({ name: "lead", source: "product" })}>
            <span>
              <b>{t("lead.forVenueTitle")}</b>
              <small>{t("lead.forVenueText")}</small>
            </span>
            <span aria-hidden="true">›</span>
          </button>
        )}

        <p className="product-detail__desc">{product.description}</p>
        {product.flavor && (product.flavor.notes?.length || product.flavor.acidity !== undefined) ? <Ornament title={t("flavor.title")} /> : null}
        <FlavorProfile flavor={product.flavor} />

        {product.attributes && Object.keys(product.attributes).length > 0 && (
          <div className="attr-grid" aria-label={t("product.specsTitle")}>
            {Object.entries(product.attributes).map(([key, value]) => (
              <div key={key}>
                <span>{key}</span>
                <strong>{value}</strong>
              </div>
            ))}
          </div>
        )}

        {(() => {
          const originText = product.attributes?.["Происхождение"] ?? Object.values(product.attributes ?? {})[0];
          const origin = product.category === "coffee" ? findOrigin(originText) : null;
          return origin ? <OriginMap origin={origin} region={originText} /> : null;
        })()}

        {product.category === "coffee" && (
          <button className={`btn btn--ghost compare-toggle ${compare.has(product.id) ? "is-on" : ""}`} onClick={() => { haptic("selection"); compare.toggle(product.id); }}>
            {compare.has(product.id) ? `✓ ${t("compare.added")}` : `⇄ ${t("compare.add")}`}
          </button>
        )}

        <div className="section-heading" style={{ marginTop: "var(--space-4)" }}>
          <span className="diamond" />
          {t("product.reviewsTitle")}
          {reviewsTotal > 0 && <span style={{ color: "var(--color-muted)", fontWeight: 600 }}>· {reviewsTotal}</span>}
        </div>
        {reviews.length === 0 ? (
          <p style={{ fontSize: 14, color: "var(--color-ink-soft)" }}>{t("product.noReviews")}</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {reviews.map((r) => (
              <ReviewCard key={r.id} review={r} />
            ))}
            {reviewsTotal > reviews.length && (
              <button className="btn btn--ghost" style={{ width: "100%" }} onClick={() => navigate({ name: "reviews", slug })}>
                {t("reviews.all", { count: reviewsTotal })}
              </button>
            )}
          </div>
        )}

        <MainAction
          glass
          text={primaryText}
          onClick={handlePrimary}
          hidden={inquiryOpen || zoomOpen || subscribeOpen}
          caption={!isUnderOrder && inStock ? <PriceTag uzs={variant.priceUzs} /> : undefined}
        />
      </main>

      {variant && <SubscribeSheet product={product} variant={variant} open={subscribeOpen} onClose={() => setSubscribeOpen(false)} />}
      <CompareBar />
      <InquirySheet product={product} open={inquiryOpen} onClose={() => setInquiryOpen(false)} mode={isUnderOrder ? "equipment" : "restock"} />

      {zoomOpen && product.images.length > 0 && (
        <div className="lightbox" role="dialog" aria-modal="true" onClick={() => setZoomOpen(false)}>
          <button className="lightbox__close" onClick={() => setZoomOpen(false)} aria-label={t("common.close")}>
            ✕
          </button>
          <img src={mediaUrl(product.images[activeImage])} alt={product.name} onClick={(e) => e.stopPropagation()} />
          {product.images.length > 1 && (
            <div className="lightbox__nav" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => setActiveImage((i) => (i - 1 + product.images.length) % product.images.length)} aria-label="‹">
                ‹
              </button>
              <span>
                {activeImage + 1} / {product.images.length}
              </span>
              <button onClick={() => setActiveImage((i) => (i + 1) % product.images.length)} aria-label="›">
                ›
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
